import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Plus } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { Button, Input, Tabs, Typography, type TabItem } from '../ui';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { contactPath, useWholeContactBook } from '../../../lib/contact-book';
import { defaultTab, type OutreachTab } from '../../../lib/contact-rows';
import { outreachUrlQuery, readOutreachUrlState, sameQuery } from '../../../lib/outreach-url-state';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useOutreachCallTabEnabled } from '../../../lib/outreach-call-tab-flag';
import { useTranslation } from '../../../lib/i18n';
import { ContactsBody, type ContactsListState } from './ContactsBody.legacy';
import { InboxBody } from './InboxBody.legacy';
import { CallsBody } from './CallsBody';
import styles from './contacts.legacy.module.css';

/**
 * The **Outreach** screen — two peer tabs, **Contacts** (the list) and **Inbox** (placeholder for a
 * later piece). Reached from the Your Agents "Outreach" tile; the route stays `/contacts` so the
 * deeplink and drill-in pages are unchanged. Shared 1:1 with iOS/Android `OutreachScreen`.
 *
 * The search + "Add a new contact" sit per-viewport, matching each Figma frame: **desktop** puts them
 * in the toolbar beside the tabs (web `17937:268706`); **mobile-web** drops the search full-width into
 * the list and pins "Add a new contact" at the foot over a gradient scrim, like the native apps
 * (`17937:264687`). The map-home Outreach section embeds `ContactsBody` directly (no tabs).
 *
 * The Contacts tab lists the WHOLE people book under the outreach-derived head (`wholeBook`). The
 * screen opens on the Inbox when there is correspondence to show and on Contacts when there is none
 * (`defaultTab`) — decided once, on the first real data, and never over a tab the reader picked.
 *
 * A third tab, **Calls** (`CallsBody`), follows Inbox behind `symphonyOutreachCallTab`: Alfred's Connect, or its
 * number once connected. It is never the default, and it carries no search and no "Add a new contact".
 */
/** `onHome`: hosted by the home's Messages category — no page header, no add button of its own (the
 *  home draws it), and the tab and searches stay out of the URL, which belongs to the home. */
export function OutreachBody({ onHome }: { onHome?: boolean } = {}) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const narrow = useNarrow();
  const { logPageClickBi } = useGenericBi();
  const [tab, setTab] = useState<OutreachTab>('contacts');
  // Two independent queries — one per tab. A single shared query leaked a Contacts search into the
  // Inbox (and back) on a tab switch; iOS models the same two fields (`search` / `inboxSearch`), and
  // each tab's search-started BI hook watches its OWN query.
  const [search, setSearch] = useState('');
  const [inboxSearch, setInboxSearch] = useState('');
  const tabPicked = useRef(false);
  const tabDecided = useRef(false);

  // The tab and both searches live in the URL (`lib/outreach-url-state.ts`), because opening a contact
  // is a page navigation that unmounts this body: without it, back from a contact opened on the
  // Contacts tab landed on the Inbox. Restored ONCE, when the router has the query — and declared
  // before the default-tab effect, so a restored tab is already decided by the time that rule runs.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || (!onHome && !router.isReady)) return;
    restored.current = true;
    if (onHome) return;
    const saved = readOutreachUrlState(router.query);
    if (saved.tab) {
      tabDecided.current = true;
      tabPicked.current = true;
      setTab(saved.tab);
    }
    if (saved.search) setSearch(saved.search);
    if (saved.inboxSearch) setInboxSearch(saved.inboxSearch);
  }, [router.isReady, router.query, onHome]);

  // The same read the Contacts tab renders (one cache entry, so nothing is fetched twice), held here
  // only to choose the opening tab. `ready` is the first real data — a cached head paints it at once,
  // else the first page does; a failed first read decides nothing, and the screen stays on Contacts.
  const book = useWholeContactBook();
  useEffect(() => {
    if (!restored.current || tabDecided.current || book.kind !== 'ready') return;
    tabDecided.current = true;
    if (!tabPicked.current) setTab(defaultTab(book.contacts));
  }, [book, router.isReady]);

  // …and written back as they change. `replace`, shallow, so it adds no history entry and refetches
  // nothing — the browser's back then returns to exactly this. No tab is written until one is decided,
  // so a fresh `/contacts` still gets the default rule.
  useEffect(() => {
    if (!restored.current || onHome) return;
    const next = outreachUrlQuery({ tab: tabDecided.current ? tab : undefined, search, inboxSearch }, router.query);
    if (sameQuery(router.query, next)) return;
    void router.replace({ pathname: router.pathname, query: next }, undefined, { shallow: true, scroll: false });
  }, [tab, search, inboxSearch, router, onHome]);
  const [listState, setListState] = useState<ContactsListState>('loading');

  // `undefined` until the gate answers. A `calls` tab restored from the URL waits for it, and falls back to
  // Contacts only once the gate says the tab is off.
  const callTab = useOutreachCallTabEnabled();
  useEffect(() => {
    if (callTab === false && tab === 'calls') setTab('contacts');
  }, [callTab, tab]);

  const tabs: TabItem<OutreachTab>[] = [
    { value: 'contacts', label: t('contacts.title') },
    { value: 'inbox', label: t('outreach.tab.inbox') },
    ...(callTab === true ? [{ value: 'calls' as const, label: t('outreach.tab.calls') }] : []),
  ];

  const addContact = () => logPageClickBi({ page_name: 'contacts_page', button_name: 'add_contact' });
  // "Add a new contact" drops the user into a fresh chat with a seeded first message — the same handoff
  // the contact detail's "Reach out" uses, so adding a contact is a conversation, not a form.
  const addContactHref = chatHandoffLink({ kind: 'new', message: t('contacts.addContactSeed') }, basePath).route;
  const addContactAction = (
    <Button href={addContactHref} startIcon={<Plus size={16} aria-hidden />} onClick={addContact}>
      {t('contacts.addContact')}
    </Button>
  );

  const onContacts = tab === 'contacts';
  // A `calls` tab (restored from the URL) renders only once the gate says yes: while it is unanswered the body
  // is empty, so a closed toggle never mounts the Calls tab or its reads, even for a moment.
  const onCalls = tab === 'calls' && callTab === true;
  const callsPending = tab === 'calls' && callTab !== true;
  // Desktop toolbar search + Add show on both LIST tabs once the active list is populated (loading and the
  // illustrated empty state carry their own chrome — a spinner, or the in-card CTA). Calls has neither.
  const showToolbarActions = !onCalls && !callsPending && !narrow && listState !== 'loading' && listState !== 'empty';
  const showToolbarAdd = showToolbarActions && !onHome;
  // Mobile-web pins the foot CTA on both list tabs: Contacts in every state, and the Inbox empty state (its
  // in-card CTA is desktop-only, matching the Contacts empty card). Not on Calls.
  const showFootCta = narrow && !onCalls && !callsPending && !onHome;
  // Nothing while a restored `calls` waits on the gate: the Calls copy must not show to someone outside it.
  const subtitle = callsPending ? null : onCalls ? t('outreach.calls.subtitle') : onContacts ? t('contacts.description') : t('outreach.inbox.subtitle');

  return (
    <div className={`${styles.body} ${styles.outreachRoot}`}>
      {!onHome && <PageHeader title={t('home.map.outreach')} flush />}

      <div className={styles.toolbar}>
        <Tabs<OutreachTab>
          items={tabs}
          value={tab}
          onChange={(value) => {
            logPageClickBi({ page_name: 'contacts_page', button_name: `tab_${value}` });
            // The reader has chosen; the default rule never overrides that — and a pick is a decision,
            // so it is written to the URL even if it beat the default rule to the first read.
            tabPicked.current = true;
            tabDecided.current = true;
            setTab(value);
          }}
          size="medium"
          label={t('home.map.outreach')}
        />
        {showToolbarActions ? (
          <div className={styles.toolbarActions}>
            <Input
              kind="search"
              type="search"
              // The toolbar draws ONE field but edits the ACTIVE tab's own query, so switching tabs
              // shows that tab's search rather than carrying the other's over.
              value={onContacts ? search : inboxSearch}
              onChange={(event) => (onContacts ? setSearch : setInboxSearch)(event.target.value)}
              onClear={() => (onContacts ? setSearch : setInboxSearch)('')}
              clearAriaLabel={t('search.dialog.clear')}
              placeholder={onContacts ? t('contacts.search.placeholder') : t('outreach.inbox.search.placeholder')}
              className={styles.toolbarSearch}
            />
            {showToolbarAdd && addContactAction}
          </div>
        ) : null}
      </div>

      {subtitle !== null ? (
        <Typography variant="p2" color="secondary" className={styles.outreachSubtitle}>
          {subtitle}
        </Typography>
      ) : null}

      {callsPending ? null : onCalls ? (
        <CallsBody />
      ) : onContacts ? (
        <ContactsBody
          onOpen={(id) => router.push(contactPath(id))}
          illustratedEmpty
          // Desktop: the CTA lives inside the empty card. Narrow: it is pinned at the foot instead.
          emptyAction={narrow ? undefined : addContactAction}
          onListState={setListState}
          search={search}
          onSearchChange={setSearch}
          // Desktop draws the search in the toolbar; narrow lets the body draw it full-width.
          renderSearch={narrow}
          wholeBook
        />
      ) : (
        <InboxBody
          onOpen={(id) => router.push(contactPath(id))}
          emptyAction={narrow ? undefined : addContactAction}
          onListState={setListState}
          search={inboxSearch}
          onSearchChange={setInboxSearch}
          renderSearch={narrow}
        />
      )}

      {/* Native-style foot CTA on mobile-web: a full-width button pinned to the bottom over a
          transparent→canvas gradient, the same treatment the Packs/Goals sticky CTAs use. */}
      {showFootCta ? (
        <div className={styles.footCta}>
          <Button fullWidth href={addContactHref} startIcon={<Plus size={16} aria-hidden />} onClick={addContact}>
            {t('contacts.addContact')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
